import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrums from "../../common/breadcrumbs";
import { Details } from "../../services/report.services";
import Loader from "../../common/loader";

const ViewReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Reports", url: "/admin/report/list/1" },
        { title: t("link_view"), url: "" },
    ];

    useEffect(() => {
        if (id) {
            Details(id)
                .then((response) => {
                    if ((response.status || response.success) && response.data) {
                        setData(response.data);
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [id]);

    if (isLoading) {
        return <Loader />;
    }

    if (!data) {
        return <div className="text-center mt-5">Report not found</div>;
    }

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        return `${process.env.REACT_APP_API_URL || ""}/${imagePath}`;
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">View Report</h6>
                            <button
                                className="btn btn-secondary"
                                onClick={() => navigate(-1)}
                            >
                                <i className="fa fa-arrow-left me-1"></i> Back
                            </button>
                        </div>

                        <div className="row">
                            <div className="col-md-4 mb-4 text-center">
                                <label className="fw-bold d-block text-start mb-2">Report Image:</label>
                                {data.report_image ? (
                                    <img
                                        src={getImageUrl(data.report_image)}
                                        alt={data.title}
                                        style={{
                                            width: "100%",
                                            maxWidth: "300px",
                                            borderRadius: "10px",
                                            objectFit: "cover",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                                        }}
                                    />
                                ) : (
                                    <div 
                                        className="d-flex align-items-center justify-content-center bg-light text-muted"
                                        style={{ width: "100%", height: "200px", borderRadius: "10px" }}
                                    >
                                        No Image
                                    </div>
                                )}

                                <label className="fw-bold d-block text-start mb-2 mt-4">Thumbnail Image:</label>
                                {data.thumbnail_image ? (
                                    <img
                                        src={getImageUrl(data.thumbnail_image)}
                                        alt={`${data.title} Thumbnail`}
                                        style={{
                                            width: "100%",
                                            maxWidth: "300px",
                                            borderRadius: "10px",
                                            objectFit: "cover",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                                        }}
                                    />
                                ) : (
                                    <div 
                                        className="d-flex align-items-center justify-content-center bg-light text-muted"
                                        style={{ width: "100%", height: "200px", borderRadius: "10px" }}
                                    >
                                        No Thumbnail
                                    </div>
                                )}
                            </div>
                            
                            <div className="col-md-8">
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th style={{ width: "30%" }}>Title</th>
                                            <td>{data.title || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Slug</th>
                                            <td>{data.slug || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td>
                                                <span className={`badge ${data.status === "Active" ? 'bg-success' : 'bg-danger'}`}>
                                                    {data.status === "Active" ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Personalized Price (INR)</th>
                                            <td>₹ {data.personalized_price_inr || "0"}</td>
                                        </tr>
                                        <tr>
                                            <th>Personalized Price (USD)</th>
                                            <td>$ {data.personalized_price_usd || "0"}</td>
                                        </tr>
                                        <tr>
                                            <th>AI Price (INR)</th>
                                            <td>₹ {data.ai_price_inr || "0"}</td>
                                        </tr>
                                        <tr>
                                            <th>AI Price (USD)</th>
                                            <td>$ {data.ai_price_usd || "0"}</td>
                                        </tr>
                                        <tr>
                                            <th>Short Description</th>
                                            <td>{data.short_description || "N/A"}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-12">
                                <h6>Detailed Description</h6>
                                <div 
                                    className="p-3 border rounded bg-light"
                                    dangerouslySetInnerHTML={{ __html: data.detailed_description || "N/A" }}
                                />
                            </div>
                        </div>

                        {data.parameters && data.parameters.length > 0 && (
                            <div className="row mt-4">
                                <div className="col-12">
                                    <h6>Report Parameters</h6>
                                    <ul className="list-group">
                                        {data.parameters.map((param, index) => (
                                            <li key={index} className="list-group-item">
                                                <i className="fa fa-check-circle text-primary me-2"></i>
                                                {param.parameter_title}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {data.questions && data.questions.length > 0 && (
                            <div className="row mt-4">
                                <div className="col-12">
                                    <h6>Report Questions & Prices</h6>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Max Words</th>
                                                    <th>Year</th>
                                                    <th>Price (INR)</th>
                                                    <th>Price (USD)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.questions.map((q, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{q.question_length || "N/A"}</td>
                                                        <td>{q.question_quantity || 1}</td>
                                                        <td>₹ {q.price_inr || 0}</td>
                                                        <td>$ {q.price_usd || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewReport;
